import * as yup from 'yup';
import {
    QUESTIONNAIRE_CONTACT_STEP,
    type CompanySetupContent,
    type CompanySetupCountry,
    type GlossaryTerm,
    type QuestionnaireQuestion
} from '../constants/company-setup.constant';

/**
 * Validation for the Company Setup Remote Config payloads. Remote Config is
 * edited by hand in the Firebase console, so every payload is checked here
 * and rejected as a whole when invalid — callers then fall back to the
 * bundled defaults instead of rendering a half-broken page.
 */

const countrySchema = yup.object({
    code: yup
        .string()
        .required()
        .matches(/^[A-Z]{2}$/),
    slug: yup
        .string()
        .required()
        .matches(/^[a-z0-9-]+$/),
    supported: yup.boolean().required()
});

const countriesSchema = yup.array(countrySchema.required()).required();

const nonEmpty = () => yup.string().trim().required();

const contentSchema = yup.object({
    faqs: yup
        .array(
            yup
                .object({
                    id: nonEmpty(),
                    question: nonEmpty(),
                    answer: nonEmpty()
                })
                .required()
        )
        .required(),
    // Free-form keys: validated by `validateGlossary` (a shapeless
    // yup.object() would have every key stripped by `stripUnknown`).
    glossary: yup
        .mixed<Record<string, unknown>>()
        .test('is-object', 'glossary must be an object', (value) =>
            typeof value === 'object' && value !== null && !Array.isArray(value)
        )
        .required(),
    services: yup
        .array(
            yup
                .object({
                    id: nonEmpty(),
                    icon: yup.string().default(''),
                    title: nonEmpty(),
                    description: nonEmpty(),
                    image: nonEmpty()
                })
                .required()
        )
        .required()
        .min(1),
    personas: yup
        .array(
            yup
                .object({
                    id: nonEmpty(),
                    icon: yup.string().default(''),
                    title: nonEmpty(),
                    description: nonEmpty(),
                    image: nonEmpty()
                })
                .required()
        )
        .required()
        .min(1),
    experts: yup
        .array(
            yup
                .object({
                    id: nonEmpty(),
                    name: nonEmpty(),
                    languagesLabel: nonEmpty(),
                    flags: yup.array(yup.string().required()).default([]),
                    calendlyUrl: yup.string().url().required()
                })
                .required()
        )
        .required(),
    questions: yup
        .array(
            yup
                .object({
                    id: nonEmpty(),
                    type: yup.mixed<'A' | 'B'>().oneOf(['A', 'B']).required(),
                    icon: yup.string().default(''),
                    title: nonEmpty(),
                    subtitle: yup.string().default(''),
                    options: yup
                        .array(
                            yup
                                .object({
                                    id: nonEmpty(),
                                    label: nonEmpty(),
                                    detail: yup.string().optional(),
                                    opsFlag: yup.boolean().optional(),
                                    next: nonEmpty()
                                })
                                .required()
                        )
                        .required()
                        .min(1)
                })
                .required()
        )
        .required()
        .min(1)
});

const parseJson = (raw: unknown): unknown => {
    if (typeof raw !== 'string') return raw;
    if (!raw.trim()) return undefined;
    try {
        return JSON.parse(raw);
    } catch {
        return undefined;
    }
};

const assertUnique = (ids: string[], label: string) => {
    const seen = new Set<string>();
    for (const id of ids) {
        if (seen.has(id)) throw new Error(`duplicate ${label} id "${id}"`);
        seen.add(id);
    }
};

const validateGlossary = (glossary: object): Record<string, GlossaryTerm> => {
    const result: Record<string, GlossaryTerm> = {};
    for (const [id, term] of Object.entries(glossary)) {
        if (!/^[a-zA-Z]+$/.test(id)) {
            throw new Error(`glossary id "${id}" must be letters only`);
        }
        const { label, definition } = (term ?? {}) as Partial<GlossaryTerm>;
        if (typeof label !== 'string' || !label.trim()) {
            throw new Error(`glossary "${id}" is missing a label`);
        }
        if (typeof definition !== 'string' || !definition.trim()) {
            throw new Error(`glossary "${id}" is missing a definition`);
        }
        result[id] = { label, definition };
    }
    return result;
};

/**
 * Number of question screens on the longest path from the first question to
 * the contact form. Throws when a `next` points nowhere or the flow loops.
 */
const longestPath = (questions: QuestionnaireQuestion[]): number => {
    const byId = new Map(questions.map((question) => [question.id, question]));
    const depth = new Map<string, number>();
    const visiting = new Set<string>();

    const visit = (id: string): number => {
        if (id === QUESTIONNAIRE_CONTACT_STEP) return 0;
        const cached = depth.get(id);
        if (cached !== undefined) return cached;
        const question = byId.get(id);
        if (!question) throw new Error(`"next" points to unknown "${id}"`);
        if (visiting.has(id)) throw new Error(`questionnaire loops at "${id}"`);
        visiting.add(id);
        const longest = Math.max(
            ...question.options.map((option) => visit(option.next))
        );
        visiting.delete(id);
        depth.set(id, longest + 1);
        return longest + 1;
    };

    // Walk every question (not just the reachable ones) so a typo in an
    // orphaned question still fails validation instead of lurking.
    questions.forEach((question) => visit(question.id));
    return visit(questions[0].id);
};

/** Question screens on the longest path + the contact form. */
export const getQuestionnaireStepCount = (
    questions: QuestionnaireQuestion[]
): number => longestPath(questions) + 1;

export const parseCompanySetupCountries = (
    raw: unknown
): CompanySetupCountry[] | null => {
    const value = parseJson(raw);
    if (value === undefined) return null;
    try {
        const countries = countriesSchema.validateSync(value, {
            stripUnknown: true
        });
        assertUnique(
            countries.map((country) => country.slug),
            'country slug'
        );
        return countries;
    } catch (error) {
        console.error('[company-setup] invalid countries config:', error);
        return null;
    }
};

export const parseCompanySetupContent = (
    raw: unknown,
    source: string
): CompanySetupContent | null => {
    const value = parseJson(raw);
    if (value === undefined) return null;
    try {
        const parsed = contentSchema.validateSync(value, {
            stripUnknown: true
        });
        const content: CompanySetupContent = {
            ...parsed,
            glossary: validateGlossary(parsed.glossary)
        };
        assertUnique(content.faqs.map((faq) => faq.id), 'faq');
        assertUnique(content.services.map((service) => service.id), 'service');
        assertUnique(content.personas.map((persona) => persona.id), 'persona');
        assertUnique(content.questions.map((question) => question.id), 'question');
        content.questions.forEach((question) =>
            assertUnique(
                question.options.map((option) => option.id),
                `option in "${question.id}"`
            )
        );
        longestPath(content.questions);
        return content;
    } catch (error) {
        console.error(`[company-setup] invalid content in ${source}:`, error);
        return null;
    }
};
